'use client';

import { ReactNode } from 'react';

interface ScoreTagProps {
  children: ReactNode;
  icon?: ReactNode;
  score?: number | null;
  variant?: 'score' | 'outline';
}

// ScoreTag shares the same base shape as JobTag but
// uses score-based colors (matching the original score badge styling)
// or an outline variant for special badges like "Immediate Joiner".
export function ScoreTag({ children, icon, score, variant = 'score' }: ScoreTagProps) {
  const hasScore = typeof score === 'number';
  const numericScore = hasScore ? score : 0;

  const getBackgroundColor = () => {
    if (variant === 'outline') return 'bg-transparent';
    if (!hasScore) return 'bg-gray-100 dark:bg-gray-800';
    if (numericScore >= 80) return 'bg-green-100/50 dark:bg-green-900/50';
    if (numericScore >= 65) return 'bg-blue-100/30 dark:bg-blue-900/50';
    if (numericScore >= 50) return 'bg-yellow-100/50 dark:bg-yellow-900/50';
    return 'bg-red-100/50 dark:bg-red-900/50';
  };

  const getTextColor = () => {
    if (variant === 'outline') return 'text-emerald-700 dark:text-emerald-400';
    if (!hasScore) return 'text-gray-600 dark:text-gray-400';
    if (numericScore >= 80) return 'text-green-700 dark:text-green-300';
    if (numericScore >= 65) return 'text-blue-700 dark:text-blue-300';
    if (numericScore >= 50) return 'text-yellow-700 dark:text-yellow-300';
    return 'text-red-700 dark:text-red-300';
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md ${
        variant === 'outline'
          ? 'border border-emerald-500 dark:border-emerald-600'
          : ''
      } ${getBackgroundColor()} ${getTextColor()}`}
    >
      {icon && (
        <span className="inline-flex h-3 w-3 flex-shrink-0 items-center justify-center overflow-hidden [&>svg]:h-3 [&>svg]:w-3">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
}

